import { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import BaseTable from "@components/DataTable/BaseTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import { Pagination } from "@components/Pagination/Pagination";
import { LANG_BASE_HEADER } from "@features/vessel-management/components/RegisteredVessels/RegisteredVessels";
import { FORM_FIELDS } from "@features/vessel-register/utils";
import type { Vessel } from "@services/vessel/types";
import { useTranslation } from "react-i18next";

type Props = {
  vessels: Vessel[];
  onClose: () => void;
  onConfirm: () => void;
  pageNumber: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  isLoading: boolean;
  totalRecordCount: number;
};

const VesselsByNameDialog = ({
  onClose,
  onConfirm,
  vessels,
  pageNumber,
  onPageChange,
  onPageSizeChange,
  pageSize,
  isLoading,
  totalRecordCount,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const headers: Header[] = useMemo(
    () => [
      { field: FORM_FIELDS.VESSEL_NAME, columnName: t(`${LANG_BASE_HEADER}.vesselName`) },
      { field: FORM_FIELDS.IMO, columnName: t(`${LANG_BASE_HEADER}.imoNo.`) },
      { field: FORM_FIELDS.YEAR_BUILT, columnName: t(`${LANG_BASE_HEADER}.yearOfBuild`) },
      { field: FORM_FIELDS.FLAG, columnName: t(`${LANG_BASE_HEADER}.flag`) },
      { field: FORM_FIELDS.SHIP_OWNER, columnName: t(`${LANG_BASE_HEADER}.shipOwner`) },
      { field: FORM_FIELDS.LENGTH_OVERALL, columnName: t(`${LANG_BASE_HEADER}.loa`) },
      { field: FORM_FIELDS.GROSS_TONNAGE, columnName: t(`${LANG_BASE_HEADER}.grt`) },
      {
        field: FORM_FIELDS.PORT_OF_REGISTRATION,
        columnName: t(`${LANG_BASE_HEADER}.portOfRegistry`),
      },
    ],
    [t],
  );

  const na = useMemo(() => t("common.notApplicable"), [t]);

  const formattedFormData = vessels?.map(
    (
      {
        vesselName,
        vesselImoNumber,
        shipBuildYear,
        country: { countryCode, countryName } = {},
        port: { portName } = {},
        shipOwnership: { shipsOwner } = {},
        loa,
        grt,
      },
      index,
    ) => {
      const flag = [countryCode, countryName].filter(Boolean).join(" - ");

      return {
        id: index,
        [FORM_FIELDS.VESSEL_NAME]: vesselName ?? na,
        [FORM_FIELDS.IMO]: vesselImoNumber ?? na,
        [FORM_FIELDS.YEAR_BUILT]: shipBuildYear ?? na,
        [FORM_FIELDS.FLAG]: flag || na,
        [FORM_FIELDS.PORT_OF_REGISTRATION]: portName ?? na,
        [FORM_FIELDS.SHIP_OWNER]: shipsOwner ?? na,
        [FORM_FIELDS.LENGTH_OVERALL]: loa ?? na,
        [FORM_FIELDS.GROSS_TONNAGE]: grt ?? na,
      };
    },
  );

  const content = useMemo(() => {
    if (isLoading) {
      return <div>{t("common.loading")}</div>;
    }

    return (
      <>
        <div style={{ marginBottom: theme.spacing(1) }}>
          <b>{t("vessel-management.vessel-name-in-use")}</b>
        </div>
        <BaseTable rows={formattedFormData} headers={headers} />
        <Pagination
          page={pageNumber}
          pageSize={pageSize}
          totalCount={totalRecordCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
        <div style={{ marginTop: theme.spacing(1) }}>
          <b>{t("vessel-management.proceed-with-registration")}</b>
        </div>
      </>
    );
  }, [isLoading, formattedFormData, headers, pageNumber, pageSize, totalRecordCount]);

  return (
    <GenericDialog
      open={true}
      title="Registered Vessels"
      cancelButtonText="Cancel"
      confirmButtonText="Proceed"
      handleCloseDialog={onClose}
      confirmAction={onConfirm}
      closeIconAction={onConfirm}
      maxWidth="lg"
      elementContent={content}
    />
  );
};

export default VesselsByNameDialog;
